import { Check, ExternalLink, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { ReactNode } from "react";

import { SearchableSelect } from "@/features/commitments/SearchableSelect";
import {
  useAccountsPickerQuery,
  usePlanYearPickerQuery,
} from "@/features/commitments/hooks";

import {
  useApplyPlaybookMutation,
  useRevertPlaybookApplicationMutation,
} from "./hooks";
import type {
  ApplyPlaybookDuplicateCandidate,
  ApplyPlaybookResponse,
} from "./api";
import type { PlaybookListRow } from "./types";


type ApplyStage =
  | { kind: "form" }
  | { kind: "confirming"; duplicate: DuplicateState }
  | { kind: "success"; result: SuccessState };


interface DuplicateState {
  candidates: ApplyPlaybookDuplicateCandidate[];
  message: string;
}


interface SuccessState {
  applicationId: string;
  createdCount: number;
  undoExpiresAt: string | null;
  reverted: boolean;
  // Captured at apply time so the success panel can deep-link the
  // user into a Commitment Tracker view scoped to the same Account
  // + Plan year — the natural follow-up after applying a Playbook.
  accountId: string;
  planYearId: string;
}


export function ApplyPlaybookModal({
  playbook,
  onClose,
}: {
  playbook: PlaybookListRow | null;
  onClose: () => void;
}) {
  const open = Boolean(playbook);
  const [accountId, setAccountId] = useState("");
  const [planYearId, setPlanYearId] = useState("");
  const [stage, setStage] = useState<ApplyStage>({ kind: "form" });
  const [serverError, setServerError] = useState<string | null>(null);
  const accountsQuery = useAccountsPickerQuery(open);
  const planYearsQuery = usePlanYearPickerQuery(
    accountId || null,
    open && Boolean(accountId),
  );
  const applyMutation = useApplyPlaybookMutation();
  const revertMutation = useRevertPlaybookApplicationMutation();

  useEffect(() => {
    if (!open) {
      setAccountId("");
      setPlanYearId("");
      setStage({ kind: "form" });
      setServerError(null);
      applyMutation.reset();
      revertMutation.reset();
    }
    // We intentionally only reset when the modal closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedAccount = (accountsQuery.data?.accounts ?? []).find(
    (account) => account.id === accountId,
  );
  const selectedPlanYear = (planYearsQuery.data?.plan_years ?? []).find(
    (planYear) => planYear.id === planYearId,
  );
  const accountOptions = useMemo(() => (
    (accountsQuery.data?.accounts ?? []).map((account) => ({
      value: account.id,
      label: account.legal_name,
      hint: account.dba_name ?? undefined,
    }))
  ), [accountsQuery.data]);
  const planYearOptions = useMemo(() => (
    (planYearsQuery.data?.plan_years ?? []).map((planYear) => ({
      value: planYear.id,
      label: planYearLabel(planYear.effective_start, planYear.effective_end),
      hint: planYear.is_current ? "Current" : undefined,
    }))
  ), [planYearsQuery.data]);

  if (!playbook) return null;

  const submitDisabled = (
    !accountId
    || !planYearId
    || applyMutation.isPending
    || stage.kind === "success"
  );

  function handleResponse(
    response: ApplyPlaybookResponse,
  ): void {
    setServerError(null);
    if (response.requires_confirmation) {
      setStage({
        kind: "confirming",
        duplicate: {
          candidates: response.duplicate_candidates ?? [],
          message: response.message ?? (
            "This Playbook may have already been applied to this "
            + "Account and Plan year."
          ),
        },
      });
      return;
    }
    if (response.application_id) {
      setStage({
        kind: "success",
        result: {
          applicationId: response.application_id,
          createdCount: response.created_count ?? 0,
          undoExpiresAt: response.undo_expires_at ?? null,
          reverted: false,
          // ``account_id`` / ``plan_year_id`` ride back on the apply
          // response (BE-046), but if a stub server omits them we
          // fall back to the ids the user picked in the form.
          accountId: response.account_id ?? accountId,
          planYearId: response.plan_year_id ?? planYearId,
        },
      });
    }
  }

  function submit({ confirmDuplicates }: { confirmDuplicates: boolean }): void {
    if (!playbook || !accountId || !planYearId) return;
    setServerError(null);
    applyMutation.mutate(
      {
        playbookId: playbook.playbook_id,
        input: {
          account_id: accountId,
          plan_year_id: planYearId,
          confirm_duplicates: confirmDuplicates,
        },
      },
      {
        onSuccess: (response) => handleResponse(response),
        onError: (err) => setServerError(
          err instanceof Error
            ? err.message
            : "Couldn't apply this Playbook. Refresh and try again.",
        ),
      },
    );
  }

  function handleRevert(): void {
    if (stage.kind !== "success") return;
    const { applicationId } = stage.result;
    revertMutation.mutate(applicationId, {
      onSuccess: () => {
        setStage((prev) => (
          prev.kind === "success"
            ? { kind: "success", result: { ...prev.result, reverted: true } }
            : prev
        ));
      },
      onError: (err) => setServerError(
        err instanceof Error
          ? err.message
          : "Couldn't revert this application.",
      ),
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="apply-playbook-title"
        className="flex w-full max-w-lg flex-col rounded-lg border border-border-subtle bg-canvas shadow-xl"
      >
        <header className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
          <div>
            <h2
              id="apply-playbook-title"
              className="text-base font-semibold text-text-primary"
            >
              Apply Playbook
            </h2>
            <p className="mt-0.5 text-xs text-text-tertiary">
              {playbook.name}
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded-md p-1 text-text-secondary hover:bg-recessed"
          >
            <X size={18} />
          </button>
        </header>
        <div className="flex flex-col gap-4 p-5 text-sm">
          {stage.kind === "success" ? (
            <SuccessPanel
              state={stage.result}
              onRevert={handleRevert}
              onClose={onClose}
              isReverting={revertMutation.isPending}
            />
          ) : (
            <>
              <Field label="Account">
                <SearchableSelect
                  fieldId="apply-playbook-account"
                  value={accountId}
                  options={accountOptions}
                  placeholder="Search accounts..."
                  triggerDisplay={
                    selectedAccount
                      ? <span>{selectedAccount.legal_name}</span>
                      : <span className="italic text-text-tertiary">Select Account</span>
                  }
                  isPending={accountsQuery.isLoading || accountsQuery.isFetching}
                  onPick={(nextAccountId) => {
                    setAccountId(nextAccountId);
                    setPlanYearId("");
                    setStage({ kind: "form" });
                  }}
                />
              </Field>

              <Field label="Plan year">
                <SearchableSelect
                  fieldId="apply-playbook-plan-year"
                  value={planYearId}
                  options={planYearOptions}
                  placeholder="Search plan years..."
                  disabled={!accountId}
                  triggerDisplay={
                    selectedPlanYear
                      ? <span>{planYearLabel(
                          selectedPlanYear.effective_start,
                          selectedPlanYear.effective_end,
                        )}</span>
                      : <span className="italic text-text-tertiary">
                          {accountId ? "Select Plan year" : "Select Account first"}
                        </span>
                  }
                  isPending={planYearsQuery.isLoading || planYearsQuery.isFetching}
                  onPick={(nextPlanYearId) => {
                    setPlanYearId(nextPlanYearId);
                    setStage({ kind: "form" });
                  }}
                />
              </Field>

              {stage.kind === "confirming" && (
                <DuplicateWarning duplicate={stage.duplicate} />
              )}
              {serverError && (
                <p
                  role="alert"
                  data-testid="apply-playbook-error"
                  className="rounded-md bg-ember-tint px-3 py-2 text-xs text-ember-ink"
                >
                  {serverError}
                </p>
              )}
            </>
          )}
        </div>
        <footer className="flex justify-end gap-2 border-t border-border-subtle px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-primary hover:bg-recessed"
          >
            {stage.kind === "success" ? "Close" : "Cancel"}
          </button>
          {stage.kind !== "success" && (
            <button
              type="button"
              data-testid="apply-playbook-submit"
              disabled={submitDisabled}
              onClick={() =>
                submit({
                  confirmDuplicates: stage.kind === "confirming",
                })
              }
              className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {applyMutation.isPending
                ? "Applying…"
                : stage.kind === "confirming"
                  ? "Apply anyway"
                  : "Apply Playbook"}
            </button>
          )}
        </footer>
      </section>
    </div>
  );
}


function DuplicateWarning({ duplicate }: { duplicate: DuplicateState }) {
  return (
    <div
      role="alert"
      data-testid="apply-playbook-duplicate-warning"
      className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
    >
      <p className="font-medium">{duplicate.message}</p>
      {duplicate.candidates.length > 0 && (
        <ul className="mt-1 list-disc pl-4">
          {duplicate.candidates.slice(0, 6).map((candidate) => (
            <li key={candidate.existing_commitment_id}>{candidate.title}</li>
          ))}
          {duplicate.candidates.length > 6 && (
            <li>…and {duplicate.candidates.length - 6} more</li>
          )}
        </ul>
      )}
      <p className="mt-1 text-[11px] text-amber-700">
        Click <span className="font-medium">Apply anyway</span> to create
        another set of Commitments.
      </p>
    </div>
  );
}


function SuccessPanel({
  state, onRevert, onClose, isReverting,
}: {
  state: SuccessState;
  onRevert: () => void;
  onClose: () => void;
  isReverting: boolean;
}) {
  // ``playbook_application_id`` scopes the Tracker to exactly the
  // rows this Apply just spawned — the precise answer to "show me
  // the Commitments I just created" even when the same Account +
  // Plan year was applied multiple times today. ``source=playbook``
  // is the broader optional filter for "all Playbook-generated
  // work" and isn't what the post-apply CTA should land on.
  const trackerHref = (
    "/dashboard/commitment-tracker"
    + `?playbook_application_id=${encodeURIComponent(state.applicationId)}`
  );
  return (
    <div
      data-testid="apply-playbook-success"
      className="flex flex-col gap-2 rounded-md border border-border-subtle bg-canvas/40 px-3 py-3"
    >
      <div className="flex items-center gap-2 text-text-primary">
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-teal text-text-inverse">
          <Check size={14} aria-hidden="true" />
        </span>
        <span className="text-sm font-medium">
          Created {state.createdCount} Commitment{state.createdCount === 1 ? "" : "s"}.
        </span>
      </div>
      {!state.reverted && (
        <Link
          to={trackerHref}
          data-testid="apply-playbook-view-commitments"
          onClick={onClose}
          className="inline-flex w-fit items-center gap-1 self-start text-xs font-medium text-teal hover:underline"
        >
          View Commitments
          <ExternalLink size={11} aria-hidden="true" />
        </Link>
      )}
      {state.reverted ? (
        <p className="text-xs text-text-tertiary">
          Application reverted — Commitments archived.
        </p>
      ) : (
        <div className="flex flex-col gap-1 text-xs text-text-tertiary">
          {state.undoExpiresAt && (
            <span>
              Undo available until {formatDateTime(state.undoExpiresAt)}.
            </span>
          )}
          <button
            type="button"
            data-testid="apply-playbook-undo"
            onClick={onRevert}
            disabled={isReverting}
            className="self-start rounded-md border border-border-subtle px-2 py-1 text-xs text-text-primary hover:bg-recessed disabled:opacity-60"
          >
            {isReverting ? "Reverting…" : "Undo"}
          </button>
        </div>
      )}
    </div>
  );
}


function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
        {label}
      </span>
      <div className="rounded-md border border-border-subtle bg-canvas/40 px-1 py-1">
        {children}
      </div>
    </div>
  );
}


function planYearLabel(start: string | null, end: string | null): string {
  if (!start && !end) return "Plan year";
  if (!start) return `Through ${formatDate(end)}`;
  if (!end) return `Starting ${formatDate(start)}`;
  return `${formatDate(start)} - ${formatDate(end)}`;
}


function formatDate(value: string | null): string {
  if (!value) return "Unknown";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}


function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
